/**
 * Tracking.js - Simple client-side tracking library
 * 
 * This library provides an easy way to track user events and send them to a tracking server.
 * It supports client ID authentication and various event types.
 */

// Helper functions
function getCookie(name) {
    const value = "; " + document.cookie;
    const parts = value.split("; " + name + "=");
    if (parts.length === 2) return parts.pop().split(";").shift();
    return "";
}

/**
 * Normalize GA client ID by stripping the GA1.x. prefix if present
 * @param {string} rawCid - The raw client ID
 * @returns {string} - Normalized client ID
 */
function normaliseGaCid(rawCid) {
    if (!rawCid) return '';
    // Strips GA1.x. prefix if present (e.g., GA1.1.12345.67890 => 12345.67890)
    return rawCid.replace(/^GA\d\.\d\./, '');
}

/**
 * Helper function to get Google Analytics client ID from cookies or storage
 * @returns {string} - GA client ID if available (normalized format)
 */
function getGoogleAnalyticsClientId() {
    // First try to get from GA's own cookie (_ga)
    let gaCid = getCookie('_ga');
    if (gaCid) {
        // Normalize GA client ID to the format needed by Measurement Protocol
        return normaliseGaCid(gaCid);
    }
    
    // Try to get from localStorage where some GA implementations store it
    try {
        const gaItems = {};
        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key.startsWith('_ga') || key.includes('ga:clientId')) {
                gaItems[key] = localStorage.getItem(key);
            }
        }
        
        if (Object.keys(gaItems).length > 0) {
            for (const [key, value] of Object.entries(gaItems)) {
                if (value && value.includes('.')) {
                    // Normalize any GA client ID found in localStorage
                    return normaliseGaCid(value);
                }
            }
        }
    } catch (e) {
        // LocalStorage might not be available or accessible
        console.warn('Could not access localStorage to find GA client ID', e);
    }
    
    // Otherwise return undefined
    return undefined;
}

/**
 * Tracker class for handling event tracking with client ID
 */
class Tracker {
    /**
     * Create a new Tracker instance
     * @param {string} clientId - The client ID for authentication
     * @param {Object} options - Optional configuration
     */
    constructor(clientId, options = {}) {
        if (!clientId) {
            console.error('Client ID is required for tracking');
            throw new Error('Client ID is required');
        }
        
        this.clientId = clientId;
        // Make a shallow copy of the options to avoid modifying the original
        this.options = Object.assign({}, options);
        
        // Only set defaults if not provided in options
        if (this.options.endpoint === undefined) {
            this.options.endpoint = '/track';
        }
        
        if (this.options.debug === undefined) {
            this.options.debug = false;
        }
        
        if (this.options.forceAlternativeTracking === undefined) {
            // Auto-detect Safari browser which has stricter CORS requirements
            const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
            this.options.forceAlternativeTracking = isSafari;
            
            if (isSafari && this.options.debug) {
                console.log('Safari detected, enabling alternative tracking methods');
            }
        }
        
        // Try to get GA client ID on initialization
        this.gaClientId = getGoogleAnalyticsClientId();
        
        if (this.options.debug) {
            console.log(`Tracker initialized with client ID: ${this.clientId}`);
            console.log(`Tracker endpoint: ${this.options.endpoint}`);
            if (this.gaClientId) {
                console.log(`Google Analytics client ID: ${this.gaClientId}`);
            } else {
                console.log('No Google Analytics client ID found');
            }
        }
    }
    
    /**
     * Track an event
     * @param {string} eventName - Name of the event to track
     * @param {Object} properties - Optional event properties
     * @returns {Promise} - Promise that resolves when tracking is complete
     */
    async track(eventName, properties = {}) {
        const allowedEvents = ['PageView', 'Lead', 'Contact', 'ViewContent', 'Purchase', 'ShopeeLead', 'LazadaLead'];
        
        if (!allowedEvents.includes(eventName)) {
            console.error('Invalid event name:', eventName);
            return Promise.reject(new Error('Invalid event name'));
        }
        
        // Wait for GA client ID to be available before proceeding
        let currentGAClientId = properties.ga_client_id || getGoogleAnalyticsClientId() || this.gaClientId;
        
        // If no GA client ID is available and gtag is present, try to get it
        if (!currentGAClientId && typeof gtag !== 'undefined' && typeof window.MEASUREMENT_ID !== 'undefined') {
            currentGAClientId = await new Promise((resolve) => {
                gtag('get', window.MEASUREMENT_ID, 'client_id', (cid) => {
                    resolve(cid ? normaliseGaCid(cid) : null);
                });
                // Timeout after 2 seconds if gtag doesn't respond
                setTimeout(() => resolve(null), 2000);
            });
        }
        
        // Get Facebook click ID from URL parameters if available
        const urlParams = new URLSearchParams(window.location.search);
        const fbclid = urlParams.get('fbclid') || "";
        
        // Get Facebook browser/click IDs from cookies
        let fbc = getCookie('_fbc') || "";
        if (!fbc && fbclid) {
            const subdomainIndex = 1; // Default for client-side
            const creationTime = new Date().getTime();
            fbc = `fb.${subdomainIndex}.${creationTime}.${fbclid}`;
        }
        const fbp = getCookie('_fbp') || "";
        
        // Extract current domain
        const domain = window.location.hostname;
        
        // Look for user ID in properties or from a cookie
        let userId = properties.user_id || getCookie('_gid') || null;
        
        // Prepare request payload (must match TrackEvent struct on server)
        const payload = {
            client_id: this.clientId,
            event: eventName,
            properties: { 
                ...properties, 
                fbc: fbc, 
                fbp: fbp 
            },
            domain: domain,
            timestamp: new Date().toISOString(),
            url: window.location.href,
            // Only include optional fields if they're present in properties
            ...(properties.currency && { currency: properties.currency }),
            ...(properties.value && { value: properties.value })
        };
        
        // Add GA client ID if available
        if (currentGAClientId) {
            payload.ga_client_id = currentGAClientId;
            if (this.options.debug) {
                console.log(`Including GA client ID in tracking: ${currentGAClientId}`);
            }
        }
        
        // Add user ID if available (for cross-device tracking)
        if (userId) {
            payload.user_id = userId;
            if (this.options.debug) {
                console.log(`Including user ID in tracking: ${userId}`);
            }
        }
        
        if (this.options.debug) {
            console.log('Tracking event:', payload);
        }
        
        try {
            if (this.options.debug) {
                console.log(`Sending request to: ${this.options.endpoint}`);
            }

            // Add timeout to avoid long-hanging requests
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout
            
            try {
                // Check if endpoint is absolute URL (starts with http:// or https://)
                const isAbsoluteUrl = /^https?:\/\//i.test(this.options.endpoint);
                
                // For absolute URLs (cross-origin), use a different approach to avoid CORS issues
                if (isAbsoluteUrl && (window.location.protocol === 'http:' && this.options.endpoint.startsWith('https://') || 
                   (this.options.forceAlternativeTracking === true))) {
                    // Use alternative tracking for cross-origin HTTPS from HTTP pages or when forced
                    if (this.options.debug) {
                        console.log('Using alternative tracking approach to avoid CORS/mixed content issues');
                    }
                    
                    // Create a Promise that resolves when the alternative tracking completes
                    return new Promise((resolve) => {
                        // Method 1: Try navigator.sendBeacon if available (modern browsers)
                        if (navigator.sendBeacon) {
                            try {
                                // Make sure beacon payload has GA client ID and user ID if available
                                const beaconPayload = { ...payload };
                                
                                const blob = new Blob([JSON.stringify(beaconPayload)], {type: 'text/plain;charset=UTF-8'});
                                const success = navigator.sendBeacon(this.options.endpoint, blob);
                                
                                if (success) {
                                    if (this.options.debug) {
                                        console.log('Beacon tracking successful');
                                        if (beaconPayload.ga_client_id) {
                                            console.log(`Beacon included GA client ID: ${beaconPayload.ga_client_id}`);
                                        }
                                    }
                                    resolve({ success: true, message: "Event tracked via beacon API" });
                                    return;
                                } else if (this.options.debug) {
                                    console.log('Beacon failed, trying alternative methods');
                                }
                            } catch (beaconError) {
                                if (this.options.debug) {
                                    console.warn('Beacon error:', beaconError);
                                }
                            }
                        }
                        
                        // Method 2: Try form submission in a hidden iframe
                        try {
                            // Create a temporary form and iframe
                            const iframe = document.createElement('iframe');
                            iframe.name = `tracking_iframe_${Date.now()}`;
                            iframe.style.display = 'none';
                            document.body.appendChild(iframe);
                            
                            const form = document.createElement('form');
                            form.method = 'POST';
                            form.action = this.options.endpoint;
                            form.target = iframe.name;
                            form.style.display = 'none';
                            
                            // Add a hidden input for the payload
                            const input = document.createElement('input');
                            input.type = 'hidden';
                            input.name = 'tracking_data';
                            input.value = JSON.stringify(payload);
                            form.appendChild(input);
                            
                            // Add explicit hidden inputs for GA client ID and user ID for redundancy
                            if (currentGAClientId) {
                                const gaInput = document.createElement('input');
                                gaInput.type = 'hidden';
                                gaInput.name = 'ga_client_id';
                                gaInput.value = currentGAClientId;
                                form.appendChild(gaInput);
                            }
                            
                            if (userId) {
                                const userInput = document.createElement('input');
                                userInput.type = 'hidden';
                                userInput.name = 'user_id';
                                userInput.value = userId;
                                form.appendChild(userInput);
                            }
                            
                            // Add form to document, submit it, then clean up
                            document.body.appendChild(form);
                            form.submit();
                            
                            setTimeout(() => {
                                if (document.body.contains(form)) document.body.removeChild(form);
                                if (document.body.contains(iframe)) document.body.removeChild(iframe);
                            }, 5000);
                            
                            if (this.options.debug) {
                                console.log('Form-based tracking attempted');
                            }
                            
                            resolve({ success: true, message: "Event tracked via form submission" });
                            return;
                        } catch (formError) {
                            if (this.options.debug) {
                                console.warn('Form tracking error:', formError);
                            }
                        }
                        
                        // Method 3: Last resort - fetch with minimal payload
                        try {
                            // Only send critical data with minimal payload
                            // Must match TrackEvent struct on server exactly
                            const minimalData = {
                                client_id: this.clientId,
                                event: eventName,
                                properties: {}, // Required field, even if empty
                                domain: domain,
                                timestamp: new Date().toISOString(),
                                url: window.location.href
                                // currency and value are optional so we omit them
                            };
                            
                            // Add GA client ID if available (critical for proper tracking)
                            if (currentGAClientId) {
                                minimalData.ga_client_id = currentGAClientId;
                            }
                            
                            // Add user ID if available
                            if (userId) {
                                minimalData.user_id = userId;
                            }
                            
                            // Use fetch with POST since server only accepts POST
                            fetch(this.options.endpoint, {
                                method: 'POST',
                                headers: {'Content-Type': 'application/json'},
                                body: JSON.stringify(minimalData),
                                mode: 'cors',
                                credentials: 'include',
                                // Use a short timeout
                                signal: AbortSignal.timeout(5000)
                            }).then(response => {
                                if (this.options.debug) {
                                    console.log('Minimal fetch tracking response:', response.status);
                                }
                            }).catch(err => {
                                if (this.options.debug) {
                                    console.warn('Minimal fetch tracking failed:', err);
                                }
                            });
                            
                            resolve({ success: true, message: "Event tracked via image (limited data)" });
                        } catch (imgError) {
                            if (this.options.debug) {
                                console.error('All tracking methods failed:', imgError);
                            }
                            resolve({ success: false, message: "All tracking methods failed" });
                        }
                    });
                }
                
                // Standard fetch for same-origin or when not dealing with mixed content
                // Determine if we're making a cross-origin request
                const endpointUrl = new URL(this.options.endpoint, window.location.origin);
                const isCrossOrigin = endpointUrl.origin !== window.location.origin;
                
                // Setup fetch options with credentials
                const fetchOptions = {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-Requested-With': 'XMLHttpRequest' // Add this header to help identify AJAX requests
                    },
                    body: JSON.stringify(payload),
                    signal: controller.signal,
                    mode: 'cors', // Explicitly set CORS mode
                    referrerPolicy: 'no-referrer-when-downgrade', // Try a less restrictive referrer policy
                    credentials: 'include' // Always include credentials for both same-origin and cross-origin requests
                };
                
                if (this.options.debug) {
                    console.log('Request will include credentials for authentication/cookies');
                    console.log('Making fetch request to:', this.options.endpoint);
                    console.log('Cross-origin request:', isCrossOrigin);
                }
                
                const response = await fetch(this.options.endpoint, fetchOptions);
                
                clearTimeout(timeoutId);
                
                if (!response.ok) {
                    let errorMessage = `HTTP error! status: ${response.status}`;
                    let errorData = {};
                    
                    try {
                        errorData = await response.json();
                        errorMessage = errorData.error || errorMessage;
                    } catch (e) {
                        // If we can't parse JSON, use the status text
                        errorMessage = `${errorMessage} - ${response.statusText}`;
                    }
                    
                    const error = new Error(errorMessage);
                    error.status = response.status;
                    error.responseData = errorData;
                    throw error;
                }
                
                const data = await response.json();
                
                if (this.options.debug) {
                    console.log('Tracking response:', data);
                }
                
                return data;
            } catch (fetchError) {
                clearTimeout(timeoutId);
                throw fetchError;
            }
        } catch (error) {
            if (error.name === 'AbortError') {
                console.error('Tracking request timed out after 10 seconds');
            } else if (error.message === 'Failed to fetch') {
                console.error(`Network error - Cannot connect to ${this.options.endpoint}. Check server status and CORS configuration.`);
            } else {
                console.error('Tracking error:', error);
            }
            
            // Add a fallback tracking mechanism for critical events
            if (this.options.debug) {
                console.log('Attempting fallback tracking via image pixel');
            }
            
            try {
                // Create minimal fallback tracking via beacon or fetch (must match TrackEvent struct on server)
                const minimalData = {
                    client_id: this.clientId,
                    event: eventName,
                    properties: {}, // Required field, can be empty for fallback
                    domain: domain,
                    timestamp: new Date().toISOString(),
                    url: window.location.href
                    // currency and value are optional
                };
                
                // Add GA client ID if available (critical for proper tracking)
                if (currentGAClientId) {
                    minimalData.ga_client_id = currentGAClientId;
                    if (this.options.debug) {
                        console.log(`Including GA client ID in fallback: ${currentGAClientId}`);
                    }
                }
                
                // Add user ID if available
                if (userId) {
                    minimalData.user_id = userId;
                }
                
                // Try sendBeacon first (better for analytics during page unload)
                if (navigator.sendBeacon) {
                    const blob = new Blob([JSON.stringify(minimalData)], {type: 'text/plain;charset=UTF-8'});
                    const beaconSent = navigator.sendBeacon(this.options.endpoint, blob);
                    if (this.options.debug) {
                        console.log('Beacon fallback:', beaconSent ? 'sent' : 'failed');
                    }
                } else {
                    // Fallback to fetch with POST
                    fetch(this.options.endpoint, {
                        method: 'POST',
                        headers: {'Content-Type': 'application/json'},
                        body: JSON.stringify(minimalData),
                        mode: 'cors',
                        credentials: 'include'
                    }).catch(err => {
                        if (this.options.debug) {
                            console.warn('Fetch fallback failed:', err);
                        }
                    });
                }
                
                if (this.options.debug) {
                    console.log('Fallback tracking attempted');
                }
            } catch (fallbackError) {
                if (this.options.debug) {
                    console.error('Fallback tracking failed:', fallbackError);
                }
            }
            
            throw error;
        }
    }
    
    /**
     * Automatically track a page view
     */
    trackPageView(additionalProperties = {}) {
        return this.track('PageView', additionalProperties);
    }
    
    /**
     * Test connectivity to the tracking server
     * @returns {Promise<boolean>} - Promise that resolves to true if connection is successful
     */
    async testConnection() {
        if (this.options.debug) {
            console.log(`Testing connection to tracking server: ${this.options.endpoint}`);
        }
        
        try {
            // Determine if we're making a cross-origin request for health endpoint
            const healthUrl = `${this.options.endpoint.split('/track')[0]}/health`;
            const healthEndpointUrl = new URL(healthUrl, window.location.origin);
            const isCrossOrigin = healthEndpointUrl.origin !== window.location.origin;
            
            // Set up fetch options with credentials
            const fetchOptions = {
                mode: 'cors',
                cache: 'no-cache',
                credentials: 'include' // Always include credentials
            };
            
            if (this.options.debug) {
                console.log('Health check will include credentials');
            }
            
            // Try HEAD request first
            try {
                const headOptions = { ...fetchOptions, method: 'HEAD' };
                const headResponse = await fetch(healthUrl, headOptions);
                
                if (headResponse.ok) {
                    if (this.options.debug) {
                        console.log('Connection test successful (HEAD)');
                    }
                    return true;
                }
            } catch (headError) {
                if (this.options.debug) {
                    console.log('HEAD request failed, trying GET');
                }
            }
            
            // Try GET request to health endpoint
            const getOptions = { ...fetchOptions, method: 'GET' };
            const getResponse = await fetch(healthUrl, getOptions);
            
            if (getResponse.ok) {
                if (this.options.debug) {
                    console.log('Connection test successful (GET)');
                }
                return true;
            } else {
                if (this.options.debug) {
                    console.error(`Connection test failed: ${getResponse.status} ${getResponse.statusText}`);
                }
                return false;
            }
        } catch (error) {
            if (this.options.debug) {
                console.error('Connection test error:', error);
                
                // Offer more specific diagnostics
                if (error.message === 'Failed to fetch') {
                    console.error(`
                        Network Error Details:
                        - Endpoint: ${this.options.endpoint}
                        - Error: Could not establish connection to the server
                        
                        Possible causes:
                        1. Server is down or unreachable
                        2. DNS resolution failure
                        3. Network firewall blocking the connection
                        4. CORS policy preventing the request
                        5. Mixed content (HTTP/HTTPS) restriction
                        
                        Try:
                        - Checking server status
                        - Verifying DNS configuration
                        - Testing directly with curl: curl -v ${this.options.endpoint}
                        - Ensuring proper CORS headers on server
                    `);
                }
            }
            return false;
        }
    }
}

// Expose the Tracker class globally
window.Tracker = Tracker;

// Add diagnostics tool for admins
window.trackerDiagnostics = async function() {
    // Make sure a tracker instance exists
    if (!window.tracker) {
        console.error('No tracker instance found.');
        return;
    }
    
    console.log('Running Tracker Diagnostics...');
    
    // Test basic browser features
    console.log('1. Browser Features:');
    console.log(' - fetch API available:', typeof fetch !== 'undefined');
    console.log(' - Promise API available:', typeof Promise !== 'undefined');
    console.log(' - async/await available:', typeof async function(){} !== 'undefined');
    console.log(' - sendBeacon available:', typeof navigator.sendBeacon !== 'undefined');
    
    // Test connection to tracking server
    console.log('\n2. Server Connection:');
    try {
        const connected = await window.tracker.testConnection();
        console.log(` - Server connection: ${connected ? 'SUCCESS' : 'FAILED'}`);
    } catch (error) {
        console.log(' - Server connection: FAILED (Error: ' + error.message + ')');
    }
    
    // CORS test
    console.log('\n3. CORS Test:');
    try {
        // Determine if the endpoint is cross-origin
        const endpointUrl = new URL(window.tracker.options.endpoint, window.location.origin);
        const isCrossOrigin = endpointUrl.origin !== window.location.origin;
        console.log(` - Cross-origin request: ${isCrossOrigin ? 'YES' : 'NO'}`);
        console.log(` - Current origin: ${window.location.origin}`);
        console.log(` - Target origin: ${endpointUrl.origin}`);
        
        // Check connection directly with different methods
        console.log(' - Testing with OPTIONS preflight...');
        try {
            const optionsResponse = await fetch(window.tracker.options.endpoint, {
                method: 'OPTIONS',
                mode: 'cors',
                credentials: 'include'
            });
            console.log(`   - OPTIONS status: ${optionsResponse.status}`);
            
            // Log CORS headers
            const corsHeaders = [
                'Access-Control-Allow-Origin',
                'Access-Control-Allow-Methods',
                'Access-Control-Allow-Headers',
                'Access-Control-Allow-Credentials'
            ];
            
            corsHeaders.forEach(header => {
                console.log(`   - ${header}: ${optionsResponse.headers.get(header) || 'not present'}`);
            });
        } catch (optionsError) {
            console.log(`   - OPTIONS request failed: ${optionsError.message}`);
        }
        
        // Try a POST with minimal payload
        console.log(' - Testing with minimal POST...');
        try {
            const minimal = {
                client_id: window.tracker.clientId,
                event: 'test',
                timestamp: new Date().toISOString()
            };
            
            const postResponse = await fetch(window.tracker.options.endpoint, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Requested-With': 'XMLHttpRequest'
                },
                body: JSON.stringify(minimal),
                mode: 'cors',
                credentials: 'include',
                referrerPolicy: 'no-referrer'
            });
            
            console.log(`   - POST status: ${postResponse.status}`);
            if (postResponse.ok) {
                console.log('   - POST succeeded');
            } else {
                const errorText = await postResponse.text();
                console.log(`   - POST failed: ${errorText}`);
            }
        } catch (postError) {
            console.log(`   - POST request failed: ${postError.message}`);
        }
        
        // Try with beacon API if available
        if (typeof navigator.sendBeacon === 'function') {
            console.log(' - Testing with sendBeacon API...');
            try {
                const beacon = {
                    client_id: window.tracker.clientId,
                    event: 'test',
                    timestamp: new Date().toISOString()
                };
                
                const blob = new Blob([JSON.stringify(beacon)], {type: 'text/plain;charset=UTF-8'});
                const beaconResult = navigator.sendBeacon(window.tracker.options.endpoint, blob);
                console.log(`   - Beacon result: ${beaconResult ? 'sent' : 'failed'}`);
            } catch (beaconError) {
                console.log(`   - Beacon request failed: ${beaconError.message}`);
            }
        }
    } catch (error) {
        console.log(' - Connection test: FAILED');
        console.log(` - Error: ${error.message}`);
    }
    
    // Check configuration
    console.log('\n4. Tracker Configuration:');
    console.log(` - Client ID: ${window.tracker.clientId}`);
    console.log(` - Endpoint: ${window.tracker.options.endpoint}`);
    console.log(` - Debug Mode: ${window.tracker.options.debug}`);
    
    // Check GA client ID
    const currentGAClientId = getGoogleAnalyticsClientId();
    console.log('\n5. Google Analytics Information:');
    console.log(` - GA Client ID: ${currentGAClientId || 'Not found'}`);
    console.log(` - GA Cookie (_ga): ${getCookie('_ga') || 'Not found'}`);
    console.log(` - GA User ID (_gid): ${getCookie('_gid') || 'Not found'}`);
    
    // List GA-related localStorage items
    try {
        const gaItems = {};
        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key.startsWith('_ga') || key.includes('ga:clientId')) {
                gaItems[key] = localStorage.getItem(key);
            }
        }
        if (Object.keys(gaItems).length > 0) {
            console.log(' - GA localStorage items:');
            for (const [key, value] of Object.entries(gaItems)) {
                console.log(`   - ${key}: ${value}`);
            }
        } else {
            console.log(' - GA localStorage items: None found');
        }
    } catch (e) {
        console.log(' - GA localStorage items: Error accessing localStorage');
    }
    
    // Domain check
    console.log('\n6. Domain Information:');
    console.log(` - Current domain: ${window.location.hostname}`);
    console.log(` - Protocol: ${window.location.protocol}`);
    console.log(` - Full URL: ${window.location.href}`);
    console.log(` - User Agent: ${navigator.userAgent}`);
    
    // Check for iframe and sandbox
    console.log('\n7. Environment:');
    console.log(` - In iframe: ${window !== top ? 'YES' : 'NO'}`);
    console.log(` - Shopify editor: ${window.Shopify && window.Shopify.designMode ? 'YES' : 'NO'}`);
    
    console.log('\nDiagnostics Complete. For help, contact support with this information.');
};
